// Public, read-only demo of the client portal experience. Mirrors the UI of
// ClientPortal.jsx but is sourced entirely from the static fixture in
// src/data/demoClient.js, so this route can be exposed without authentication
// and never touches Firestore.

import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressBar, Card, Button, Input, ThemeToggle } from '../components/ui';
import {
  DocumentTextIcon, ArrowTopRightOnSquareIcon, ChartBarIcon,
  ChevronDownIcon, ChevronRightIcon, CalendarIcon,
  MagnifyingGlassIcon, LightBulbIcon, BeakerIcon, RocketLaunchIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { calcPhaseProgress } from '../hooks/useClients';
import {
  demoClient, demoPhases, demoDocuments,
  demoSurveys, demoDatabaseLookup, demoUser,
} from '../data/demoClient';

// ── Shared style maps (mirror ClientPortal) ──────────────────────────────────

const PHASE_ICONS = {
  discovery:      MagnifyingGlassIcon,
  strategy:       LightBulbIcon,
  pilot_planning: BeakerIcon,
  execution:      RocketLaunchIcon,
};

const STATUS = {
  not_started: {
    label: 'Not Started',
    badgeCls: 'bg-border-light text-text-muted-light dark:bg-surface-dark dark:text-text-muted',
    barCls:   'bg-border-light dark:bg-border',
    iconBg:   'bg-background-gray dark:bg-surface-dark',
    iconColor:'text-text-muted-light dark:text-text-muted',
  },
  in_progress: {
    label: 'In Progress',
    badgeCls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    barCls:   'bg-brand-primary',
    iconBg:   'bg-blue-100 dark:bg-blue-900/30',
    iconColor:'text-blue-600 dark:text-blue-400',
  },
  completed: {
    label: 'Completed',
    badgeCls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    barCls:   'bg-green-500',
    iconBg:   'bg-green-100 dark:bg-green-900/30',
    iconColor:'text-green-600 dark:text-green-400',
  },
};

const DELIVERABLE_STATUS_CLS = {
  pending:     'bg-border-light text-text-muted-light dark:bg-surface-dark dark:text-text-muted',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  in_review:   'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  delivered:   'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};
const DELIVERABLE_STATUS_LABELS = {
  pending: 'Pending', in_progress: 'In Progress', in_review: 'In Review', delivered: 'Delivered',
};
const DELIVERABLE_DOT = {
  pending:     'bg-border dark:bg-border',
  in_progress: 'bg-brand-primary',
  in_review:   'bg-amber-400',
  delivered:   'bg-green-500',
};

const ALL_TABS = [
  { id: 'overview',   label: 'Overview' },
  { id: 'engagement', label: 'Our Engagement' },
  { id: 'documents',  label: 'Documents' },
  { id: 'surveys',    label: 'Surveys' },
  { id: 'workshops',  label: 'Workshops' },
  { id: 'billing',    label: 'Company & Billing' },
];

// ── Demo banner ──────────────────────────────────────────────────────────────

function DemoBanner() {
  const navigate = useNavigate();
  return (
    <div className="bg-brand-primary/10 border-b border-brand-primary/30">
      <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-brand-primary text-white flex-shrink-0">
            DEMO
          </span>
          <p className="text-xs text-text-secondary-light dark:text-text-secondary truncate">
            You are viewing a live, read-only demo of the Prism client portal. All data shown is illustrative.
          </p>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="text-xs font-medium text-brand-primary hover:underline cursor-pointer flex-shrink-0"
        >
        </button>
      </div>
    </div>
  );
}

// ── Read-only phase card (identical to ClientPortal) ─────────────────────────

function ReadOnlyPhaseCard({ phase, isLast }) {
  const [open, setOpen] = useState(phase.status === 'in_progress');
  const s = STATUS[phase.status] || STATUS.not_started;
  const PhaseIcon = PHASE_ICONS[phase.phaseId] || RocketLaunchIcon;

  const completedActivities = phase.activities?.filter(a => a.completed).length ?? 0;
  const totalActivities     = phase.activities?.length ?? 0;
  const deliveredCount      = phase.deliverables?.filter(d => d.status === 'delivered').length ?? 0;
  const totalDeliverables   = phase.deliverables?.length ?? 0;

  return (
    <div className={`relative ${!isLast ? 'pb-3' : ''}`}>
      {!isLast && (
        <div className="absolute left-5 top-[52px] bottom-0 w-px bg-border-light dark:bg-border" />
      )}

      <div className="relative z-10 flex items-start gap-4 p-5 rounded-xl border border-border-light dark:border-border bg-white dark:bg-surface-dark">
        <button
          className={`w-10 h-10 min-w-[40px] rounded-xl flex items-center justify-center ${s.iconBg} hover:opacity-80 transition cursor-pointer`}
          onClick={() => setOpen(o => !o)}
        >
          <PhaseIcon className={`w-5 h-5 ${s.iconColor}`} />
        </button>

        <button className="flex-1 min-w-0 text-left cursor-pointer" onClick={() => setOpen(o => !o)}>
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider">
                Phase {phase.order}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.badgeCls}`}>
                {s.label}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {phase.startDate && (
                <span className="text-xs text-text-muted-light dark:text-text-muted hidden sm:block">
                  {phase.startDate}{phase.endDate ? ` → ${phase.endDate}` : ''}
                </span>
              )}
              {open
                ? <ChevronDownIcon  className="w-4 h-4 text-text-muted-light dark:text-text-muted" />
                : <ChevronRightIcon className="w-4 h-4 text-text-muted-light dark:text-text-muted" />
              }
            </div>
          </div>

          <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-2">{phase.name}</h3>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-background-gray dark:bg-background-gray rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${s.barCls}`}
                style={{ width: `${phase.progress || 0}%` }}
              />
            </div>
            <span className="text-xs font-medium text-text-muted-light dark:text-text-muted w-8 text-right flex-shrink-0">
              {phase.progress || 0}%
            </span>
          </div>

          <div className="flex gap-4 mt-2">
            {totalActivities > 0 && (
              <span className="text-xs text-text-muted-light dark:text-text-muted">
                {completedActivities}/{totalActivities} activities
              </span>
            )}
            {totalDeliverables > 0 && (
              <span className="text-xs text-text-muted-light dark:text-text-muted">
                {deliveredCount}/{totalDeliverables} delivered
              </span>
            )}
          </div>
        </button>
      </div>

      {open && (
        <div className="ml-14 mt-2 rounded-xl border border-border-light dark:border-border overflow-hidden bg-white dark:bg-surface-dark">
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border-light dark:divide-border">
            <div className="p-4">
              <h4 className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${s.barCls}`} />
                Activities
              </h4>
              <ul className="space-y-1.5">
                {phase.activities?.map(a => (
                  <li
                    key={a.id}
                    className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-background-light dark:bg-background border border-border-light dark:border-border"
                  >
                    <div className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      a.completed ? 'bg-green-500 border-green-500' : 'border-border-light dark:border-border'
                    }`}>
                      {a.completed && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-xs leading-relaxed ${
                      a.completed
                        ? 'line-through text-text-muted-light dark:text-text-muted'
                        : 'text-text-primary-light dark:text-text-primary'
                    }`}>
                      {a.title}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-4">
              <h4 className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-primary" />
                Deliverables
              </h4>
              <ul className="space-y-1.5">
                {phase.deliverables?.map(d => (
                  <li key={d.id} className="px-3 py-2.5 rounded-lg bg-background-light dark:bg-background border border-border-light dark:border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DELIVERABLE_DOT[d.status] || DELIVERABLE_DOT.pending}`} />
                      <span className="text-xs text-text-primary-light dark:text-text-primary flex-1 leading-relaxed">{d.title}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DELIVERABLE_STATUS_CLS[d.status] || DELIVERABLE_STATUS_CLS.pending}`}>
                      {DELIVERABLE_STATUS_LABELS[d.status] || d.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main demo portal component ───────────────────────────────────────────────

export default function DemoPortal() {
  const { tab } = useParams();
  const navigate = useNavigate();

  const client       = demoClient;
  const phases       = demoPhases;
  const allDocuments = demoDocuments;
  const allSurveys   = demoSurveys;
  const dbLookup     = demoDatabaseLookup;

  const TABS = ALL_TABS;
  const validTabIds = TABS.map((t) => t.id);
  const activeTab = validTabIds.includes(tab) ? tab : 'overview';

  const documents = useMemo(
    () => allDocuments.filter((d) => d.category !== 'internal'),
    [allDocuments],
  );

  const currentPhase    = phases.find((p) => p.status === 'in_progress');
  const allActivities   = phases.flatMap(p => p.activities || []);
  const allDeliverables = phases.flatMap(p => p.deliverables || []);
  const engProgress     = calcPhaseProgress(allActivities, allDeliverables);
  const completedPhases = phases.filter(p => p.status === 'completed').length;

  const firstName = demoUser.displayName.split(' ')[0];

  return (
    <div className="min-h-screen bg-background-light dark:bg-background">
      <ThemeToggle className="fixed top-0 right-2" />
      <div className="max-w-5xl mx-auto px-4 py-4">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
            {client.name}
          </h1>
          <p className="text-text-muted-light dark:text-text-muted mt-1">
            Welcome back, {firstName}!
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-border-light dark:border-border mb-8">
          <div className="flex overflow-x-auto gap-0">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => navigate(t.id === 'overview' ? '/demo' : `/demo/${t.id}`)}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition cursor-pointer ${
                  activeTab === t.id
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-text-secondary-light dark:text-text-secondary hover:opacity-75'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ─── OVERVIEW ─── */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <Card
              className="p-5 cursor-pointer hover:opacity-75 transition"
              onClick={() => navigate('/demo/engagement')}
            >
              <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-3">Engagement Progress</h3>
              <div className="flex items-center gap-4 mb-3">
                <span className="text-4xl font-bold text-brand-primary">{engProgress}%</span>
                <div className="flex-1">
                  <ProgressBar value={engProgress} colorClass="bg-brand-primary" />
                  <p className="text-xs text-text-muted-light dark:text-text-muted mt-1">
                    {completedPhases} of {phases.length} phases complete
                  </p>
                </div>
              </div>
              {currentPhase && (
                <p className="text-sm text-text-secondary-light dark:text-text-secondary">
                  Current phase: <span className="font-medium text-text-primary-light dark:text-text-primary">{currentPhase.name}</span>
                </p>
              )}
            </Card>

            {/* AI Adoption Insights — demo placeholder (insights page is auth-gated) */}
            <Card className="p-5 opacity-75">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                    <ChartBarIcon className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary-light dark:text-text-primary">AI Adoption Insights</h3>
                    <p className="text-xs text-text-muted-light dark:text-text-muted mt-0.5">
                      Your organisation's AI readiness data and benchmarks (available to signed-in users)
                    </p>
                  </div>
                </div>
                <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-border-light text-text-muted-light dark:bg-surface-dark dark:text-text-muted flex-shrink-0">
                  Sign in to view
                </span>
              </div>
            </Card>

            {/* Book a Workshop CTA */}
            <Card
              className="p-5 cursor-pointer hover:opacity-75 transition"
              onClick={() => navigate('/book')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                    <CalendarIcon className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary-light dark:text-text-primary">Book a Workshop</h3>
                    <p className="text-xs text-text-muted-light dark:text-text-muted mt-0.5">
                      Schedule your next AI workshop with the SeyAI team
                    </p>
                  </div>
                </div>
                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-text-muted-light dark:text-text-muted flex-shrink-0" />
              </div>
            </Card>
          </div>
        )}

        {/* ─── OUR ENGAGEMENT ─── */}
        {activeTab === 'engagement' && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Overall Progress', value: `${engProgress}%` },
                { label: 'Activities Done',  value: `${allActivities.filter(a => a.completed).length}/${allActivities.length}` },
                { label: 'Deliverables',     value: `${allDeliverables.filter(d => d.status === 'delivered').length}/${allDeliverables.length}` },
              ].map(kpi => (
                <div key={kpi.label} className="p-4 rounded-xl border border-border-light dark:border-border bg-white dark:bg-surface-dark">
                  <p className="text-xl font-bold text-text-primary-light dark:text-text-primary">{kpi.value}</p>
                  <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mt-0.5">{kpi.label}</p>
                </div>
              ))}
            </div>

            <div>
              <p className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider mb-4">
                {completedPhases}/{phases.length} phases complete
              </p>
              <div>
                {phases.map((phase, idx) => (
                  <ReadOnlyPhaseCard
                    key={phase.phaseId}
                    phase={phase}
                    isLast={idx === phases.length - 1}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── DOCUMENTS ─── */}
        {activeTab === 'documents' && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="group flex items-center gap-3 p-4 rounded-xl border border-border-light dark:border-border bg-white dark:bg-surface-dark"
                >
                  <DocumentTextIcon className="w-8 h-8 text-brand-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary-light dark:text-text-primary truncate">{doc.name}</p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted capitalize">
                      {doc.category?.replace('_', ' ')}
                    </p>
                  </div>
                  <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-border-light text-text-muted-light dark:bg-surface-dark dark:text-text-muted flex-shrink-0">
                    Demo
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-text-muted-light dark:text-text-muted mt-4">
              In a live engagement, each document opens in a new tab and is downloadable.
            </p>
          </div>
        )}

        {/* ─── SURVEYS ─── */}
        {activeTab === 'surveys' && (
          <DemoSurveysTab surveys={allSurveys} dbLookup={dbLookup} />
        )}

        {/* ─── WORKSHOPS ─── */}
        {activeTab === 'workshops' && (
          <div className="text-center py-16">
            <CalendarIcon className="w-10 h-10 mx-auto mb-2 text-text-muted-light dark:text-text-muted opacity-30" />
            <p className="text-text-muted-light dark:text-text-muted">No workshop bookings yet.</p>
            <p className="text-xs text-text-muted-light dark:text-text-muted mt-2">
              In a live engagement, confirmed and pending workshops appear here with calendar links.
            </p>
          </div>
        )}

        {/* ─── COMPANY & BILLING ─── */}
        {activeTab === 'billing' && (
          <DemoBillingTab client={client} />
        )}
      </div>
    </div>
  );
}

// ── Surveys tab (demo, copy-link works, view-survey is disabled) ─────────────

function DemoSurveysTab({ surveys, dbLookup = {} }) {
  const openSurveys = surveys.filter((s) => s.isOpen);

  if (openSurveys.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-text-muted-light dark:text-text-muted">No active surveys at the moment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {openSurveys.map((survey) => {
        const url = `https://prism.seytravel.com/survey/${survey.id}`;
        return (
          <div
            key={survey.id}
            className="bg-white dark:bg-surface-dark border border-border-light dark:border-border rounded-xl p-5"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-text-primary-light dark:text-text-primary">{survey.title}</h3>
                  {survey.databaseId && dbLookup[survey.databaseId] && (
                    <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                      {dbLookup[survey.databaseId]}
                    </span>
                  )}
                  {survey.tags?.includes('Pre') && (
                    <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      Pre
                    </span>
                  )}
                  {survey.tags?.includes('Post') && (
                    <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      Post
                    </span>
                  )}
                </div>
                {survey.description && (
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary mt-1">{survey.description}</p>
                )}
                <p className="text-xs text-text-muted-light dark:text-text-muted mt-1">
                  {survey.responseCount ?? 0} response{survey.responseCount !== 1 ? 's' : ''}
                  {survey.createdAt ? ` · Created ${new Date(survey.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="flex items-center gap-1.5 text-sm font-medium text-text-muted-light dark:text-text-muted border border-border-light dark:border-border rounded-lg px-3 py-1.5 opacity-60 cursor-not-allowed">
                  <EyeIcon className="w-4 h-4" />
                  View Survey
                </span>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 bg-background-light dark:bg-background rounded-lg px-3 py-2 border border-border-light dark:border-border">
              <span className="text-xs text-text-muted-light dark:text-text-muted font-mono truncate flex-1">{url}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Billing tab (demo, all fields read-only) ─────────────────────────────────

const noop = () => {};

function DemoBillingTab({ client }) {
  const [billing] = useState({
    billingName:    client.billingName    || '',
    billingEmail:   client.billingEmail   || '',
    billingAddress: client.billingAddress || '',
    vatNumber:      client.vatNumber      || '',
  });

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-4">Company Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
          {[
            { label: 'Name',           value: client.name },
            { label: 'Industry',       value: client.industry },
            { label: 'Village / Town', value: client.village },
            { label: 'Size',           value: client.organisationSize },
            { label: 'Website',        value: client.website },
          ].map(({ label, value }) => value ? (
            <div key={label}>
              <p className="text-xs text-text-muted-light dark:text-text-muted">{label}</p>
              <p className="font-medium text-text-primary-light dark:text-text-primary mt-0.5">{value}</p>
            </div>
          ) : null)}
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold text-text-primary-light dark:text-text-primary mb-1">Billing Details</h3>
        <p className="text-xs text-text-muted-light dark:text-text-muted mb-4">
          Used for invoicing purposes. No payment is taken through this portal.
        </p>
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-xs font-medium text-text-muted-light dark:text-text-muted uppercase tracking-wider block mb-1">Billing Name</label>
            <Input type="text" value={billing.billingName} onChange={noop} readOnly disabled className="w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted-light dark:text-text-muted uppercase tracking-wider block mb-1">Billing Email</label>
            <Input type="email" value={billing.billingEmail} onChange={noop} readOnly disabled className="w-full" />
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted-light dark:text-text-muted uppercase tracking-wider block mb-1">Billing Address</label>
            <textarea
              value={billing.billingAddress}
              onChange={noop}
              readOnly
              disabled
              rows={3}
              className="w-full rounded-xl border border-border-light dark:border-border bg-white dark:bg-background px-4 py-3 text-sm resize-none focus:outline-none opacity-100 cursor-not-allowed text-text-muted-light dark:text-text-muted"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-text-muted-light dark:text-text-muted uppercase tracking-wider block mb-1">VAT Number</label>
            <Input type="text" value={billing.vatNumber} onChange={noop} readOnly disabled className="w-full" />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Button variant="primary" disabled>
              Save Billing Details
            </Button>
            <span className="text-xs text-text-muted-light dark:text-text-muted">Editing is disabled in the demo.</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
